import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import React, { Fragment, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { TYPE_FIELD_LIST } from "../../helper/TypeFieldList";
import DynamicSelectComp from "./DynamicSelectComp";
import { Add, Close, Visibility, VisibilityOff } from "@mui/icons-material";
import { NumericFormat } from "react-number-format";
import convertCurrency from "src/helper/convertCurrency";
import PhoneNumberInput from "./PhoneNumberInput";
import FieldLabel from "./FieldLabel";

export default function ReactHookFields({
  control,
  errors,
  listFields,
  onSubmit,
  txtConfirmBtn,
  btnFullWidth,
  isLoading,
  fields,
  remove,
  append,
  watch,
  getValues,
  btnColor,
  hiddenButton = false,
  actionBtn,
  ...props
}) {
  const [showPassword, setShowPassword] = useState({});
  const watchedValues = useWatch({ control });

  const handleClickShowPassword = (fieldName) => {
    setShowPassword((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const shouldDisplayField = (field) => {
    if (field.display === "conditional") {
      const watchedValue = watchedValues[field.showWhen];
      return watchedValue === field.showValue;
    }
    return [undefined, true]?.includes(field?.display);
  };

  return (
    <Grid
      xs={12}
      item
      {...props}
    >
      <form onSubmit={onSubmit}>
        <Grid
          container
          spacing={2}
        >
          {listFields?.map((v) => {
            if (!shouldDisplayField(v)) return null;

            // Non-array field
            if (v.typeField !== TYPE_FIELD_LIST.ARRAY) {
              return (
                <Grid
                  item
                  key={v.id}
                  xs={v.grid[0] ?? 12}
                  md={v.grid[1] ?? 12}
                  align={[TYPE_FIELD_LIST.BTN, TYPE_FIELD_LIST.ICONBTN].includes(v.typeField) ? "right" : "left"}
                >
                  {isLoading ? (
                    <Skeleton
                      width="100%"
                      height="56px"
                    />
                  ) : (
                    <>
                      {/* TextField */}
                      {v.typeField === TYPE_FIELD_LIST.TEXTFIELD && (v?.show !== false || v?.watchCompare === watch(v?.watch)) && (
                        <>
                          {!!v?.txtLabel && (
                            <Typography
                              mb="12px"
                              variant="subtitle1"
                              color={props?.colorText ?? "black"}
                            >
                              <FieldLabel
                                label={v?.txtLabel}
                                required={v?.required ?? v?.require}
                              />
                            </Typography>
                          )}

                          <Controller
                            name={v.name}
                            control={control}
                            disabled={v?.require}
                            defaultValue={v.defaultValue}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                autoFocus={!!v?.autoFocus}
                                type={v.type === "password" ? (showPassword[v.name] ? "text" : "password") : (v.type ?? "text")}
                                label={
                                  !v?.txtLabel ? (
                                    <FieldLabel
                                      label={v.label}
                                      required={v?.required ?? v?.require}
                                    />
                                  ) : null
                                }
                                slotProps={{
                                  input: {
                                    sx: { color: props?.colorText ?? "black" },
                                    endAdornment: v.type === "password" && (
                                      <InputAdornment
                                        position="end"
                                        color="inherit"
                                        sx={{ color: props?.colorText ?? "black" }}
                                      >
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() => handleClickShowPassword(v.name)}
                                          onMouseDown={handleMouseDownPassword}
                                          edge="end"
                                          color="inherit"
                                        >
                                          {showPassword[v.name] ? <VisibilityOff color="inherit" /> : <Visibility color="inherit" />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                    startAdornment: v?.prefix ? (
                                      <InputAdornment
                                        position="start"
                                        sx={{ color: props?.colorText ?? "black" }}
                                      >
                                        {v?.prefix}
                                      </InputAdornment>
                                    ) : null,
                                    color: props?.colorText ?? "black",
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: props?.colorText,
                                    },
                                    "&:hover fieldset": {
                                      borderColor: props?.colorText,
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: props?.colorText,
                                    },
                                  },
                                }}
                                placeholder={v.placeHolder}
                                variant="outlined"
                                error={!!errors[v.name]}
                                helperText={errors[v.name]?.message || ""}
                                disabled={v.disabled}
                              />
                            )}
                          />
                        </>
                      )}

                      {/* MONEY */}
                      {v.typeField === TYPE_FIELD_LIST.MONEY && (
                        <Box
                          display="flex"
                          flexDirection="column"
                        >
                          <Controller
                            name={v.name}
                            control={control}
                            defaultValue={v.defaultValue ?? ""}
                            render={({ field }) => (
                              <NumericFormat
                                {...field}
                                fullWidth
                                label={
                                  <FieldLabel
                                    label={v.label}
                                    required={v?.required ?? v?.require}
                                  />
                                }
                                InputProps={{
                                  inputProps: { min: 0 },
                                  // Không bind nội dung adornment theo value — tránh tạo object InputProps mới mỗi lần gõ → mất focus
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography component="span">đ</Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                thousandSeparator
                                customInput={TextField}
                                error={!!errors[v.name]}
                                helperText={errors[v.name]?.message || ""}
                                onValueChange={(values) => field.onChange(values.floatValue ?? "")}
                              />
                            )}
                          />{" "}
                          {watch && watch(v.name) && (
                            <Typography
                              variant="body2"
                              mt={1}
                              sx={{ fontWeight: "bold", fontStyle: "italic" }}
                            >
                              {convertCurrency(Number(watch(v.name)?.toString().replace(/[^\d]/g, "") || 0))} đồng
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* SELECT */}
                      {v.typeField === TYPE_FIELD_LIST.SELECT && (
                        <>
                          {!!v?.txtLabel && (
                            <Typography
                              mb="12px"
                              variant="subtitle1"
                            >
                              <FieldLabel
                                label={v?.txtLabel}
                                required={v?.required ?? v?.require}
                              />
                            </Typography>
                          )}

                          <FormControl
                            fullWidth
                            error={!!errors[v.name]}
                          >
                            <InputLabel id={`select-${v.name}`}>
                              <FieldLabel
                                label={v.label}
                                required={v?.required ?? v?.require}
                              />
                            </InputLabel>
                            <Controller
                              name={v.name}
                              control={control}
                              defaultValue={v.defaultValue}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  labelId={`select-${v.name}`}
                                >
                                  {v.dataSelect?.length ? (
                                    v.dataSelect.map((item) => (
                                      <MenuItem
                                        key={item.id}
                                        value={item.id}
                                      >
                                        {item.name}
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem value={undefined}>Không có dữ liệu</MenuItem>
                                  )}
                                </Select>
                              )}
                            />
                            <FormHelperText>{errors[v.name]?.message}</FormHelperText>
                          </FormControl>
                        </>
                      )}

                      {/* DYNAMIC_SELECT */}
                      {v.typeField === TYPE_FIELD_LIST.DYNAMIC_SELECT && (
                        <>
                          {!!v?.txtLabel && (
                            <Typography
                              mb="12px"
                              variant="subtitle1"
                            >
                              <FieldLabel
                                label={v?.txtLabel}
                                required={v?.required ?? v?.require}
                              />
                            </Typography>
                          )}

                          <DynamicSelectComp
                            {...v}
                            control={control}
                            errors={errors}
                            disabled={v.disabled}
                          />
                          <FormHelperText sx={{ color: "#d32f2f !important" }}>{errors[v.name]?.id?.message || errors[v.name]?.message}</FormHelperText>
                        </>
                      )}

                      {/* CHECKBOX */}
                      {v.typeField === TYPE_FIELD_LIST.CHECKBOX && (
                        <FormControlLabel
                          control={
                            <Controller
                              name={v.name}
                              control={control}
                              defaultValue={v.defaultValue}
                              render={({ field }) => <Checkbox {...field} />}
                            />
                          }
                          label={
                            <FieldLabel
                              label={v.label}
                              required={v?.required ?? v?.require}
                            />
                          }
                        />
                      )}

                      {/* PHONE_NUMBER */}
                      {v.typeField === TYPE_FIELD_LIST.PHONE_NUMBER && (
                        <PhoneNumberInput
                          control={control}
                          name={v.name}
                          defaultValue={v.defaultValue}
                          defaultPhoneCode={v.defaultPhoneCode}
                          label={v.label}
                          required={v?.required ?? v?.require}
                        />
                      )}

                      {/* RADIO */}
                      {v.typeField === TYPE_FIELD_LIST.RADIO && (
                        <FormControl error={!!errors[v.name]}>
                          <FormLabel>
                            <FieldLabel
                              label={v.label}
                              required={v?.required ?? v?.require}
                            />
                          </FormLabel>
                          <Controller
                            name={v.name}
                            control={control}
                            defaultValue={v.defaultValue}
                            render={({ field }) => (
                              <RadioGroup
                                {...field}
                                row
                              >
                                {v.child?.map((c) => (
                                  <FormControlLabel
                                    key={c.id}
                                    value={c.name}
                                    disabled={v.disabled || c.disabled}
                                    control={<Radio />}
                                    label={
                                      <FieldLabel
                                        label={c.label}
                                        required={c?.required ?? c?.require}
                                      />
                                    }
                                  />
                                ))}
                              </RadioGroup>
                            )}
                          />
                          <FormHelperText>{errors[v.name]?.message}</FormHelperText>
                        </FormControl>
                      )}
                    </>
                  )}
                </Grid>
              );
            }

            // ARRAY field
            return (
              <Grid
                item
                key={v.id}
                xs={v.grid[0] ?? 12}
                md={v.grid[1] ?? 12}
              >
                {v.label && (
                  <Typography
                    sx={{
                      textAlign: "left",
                      fontWeight: "bold",
                      m: "0 0 12px",
                      pt: "16px",
                      fontSize: "16px",
                      borderTop: "1px solid var(--grey)",
                    }}
                  >
                    {v.label}
                  </Typography>
                )}
                {!!fields &&
                  fields[v.name]?.map((fieldRow, index) => (
                    <Fragment key={fieldRow.id ?? index}>
                      {index !== 0 && (
                        <IconButton
                          onClick={() => remove(index)}
                          sx={{ width: 24, height: 24, mb: 2 }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      <Grid
                        container
                        spacing={2}
                      >
                        {v.child?.map((child) => {
                          const errorItem = errors[v.name]?.[index]?.[child.name];
                          return (
                            <Grid
                              item
                              xs={child.grid[0]}
                              key={child.id}
                            >
                              {child.typeField === TYPE_FIELD_LIST.TEXTFIELD && (
                                <>
                                  {!!child?.txtLabel && (
                                    <Typography
                                      mb="12px"
                                      variant="subtitle1"
                                    >
                                      <FieldLabel
                                        label={child?.txtLabel}
                                        required={child?.required ?? child?.require}
                                      />
                                    </Typography>
                                  )}
                                  <Controller
                                    name={`${child.arrayName}.${index}.${child.name}`}
                                    control={control}
                                    defaultValue={child.defaultValue}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        fullWidth
                                        label={
                                          !child?.txtLabel ? (
                                            <FieldLabel
                                              label={child.label}
                                              required={child?.required ?? child?.require}
                                            />
                                          ) : null
                                        }
                                        placeholder={child.placeHolder}
                                        variant="outlined"
                                        error={!!errorItem}
                                        helperText={errorItem?.message}
                                        disabled={child.disabled}
                                      />
                                    )}
                                  />
                                </>
                              )}

                              {child.typeField === TYPE_FIELD_LIST.DYNAMIC_SELECT && (
                                <>
                                  <DynamicSelectComp
                                    index={index}
                                    item={child}
                                    control={control}
                                    errors={errors}
                                    disabled={child.disabled}
                                    fieldName={v?.name}
                                  />
                                  <FormHelperText sx={{ color: "#d32f2f !important" }}>{errorItem?.message}</FormHelperText>
                                </>
                              )}

                              {child.typeField === TYPE_FIELD_LIST.MONEY && (
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                >
                                  <Controller
                                    name={`${child.arrayName}.${index}.${child.name}`}
                                    control={control}
                                    defaultValue={child.defaultValue}
                                    render={({ field }) => (
                                      <NumericFormat
                                        fullWidth
                                        label={
                                          <FieldLabel
                                            label={child.label}
                                            required={child?.required ?? child?.require}
                                          />
                                        }
                                        InputProps={{
                                          inputProps: { min: 0 },
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Typography component="span">đ</Typography>
                                            </InputAdornment>
                                          ),
                                        }}
                                        thousandSeparator
                                        customInput={TextField}
                                        error={!!errorItem}
                                        helperText={errorItem?.message}
                                        value={field.value ?? ""}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        getInputRef={field.ref}
                                        onValueChange={(values) => field.onChange(values.floatValue ?? "")}
                                      />
                                    )}
                                  />
                                  {watch && watch(`${child.arrayName}.${index}.${child.name}`) && (
                                    <Typography
                                      variant="body2"
                                      mt={1}
                                      sx={{ fontWeight: "bold", fontStyle: "italic" }}
                                    >
                                      {convertCurrency(Number(watch(`${child.arrayName}.${index}.${child.name}`)?.toString().replace(/[^\d]/g, "") || 0))} đồng
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {child.typeField === TYPE_FIELD_LIST.CHECKBOX && (
                                <FormControlLabel
                                  control={
                                    <Controller
                                      name={`${child.arrayName}.${index}.${child.name}`}
                                      control={control}
                                      defaultValue={child.defaultValue}
                                      render={({ field }) => <Checkbox {...field} />}
                                    />
                                  }
                                  label={
                                    <FieldLabel
                                      label={child.label}
                                      required={child?.required ?? child?.require}
                                    />
                                  }
                                />
                              )}
                            </Grid>
                          );
                        })}
                        <Grid item>
                          <Typography color="error">{errors[v.name]?.message}</Typography>
                        </Grid>
                      </Grid>
                    </Fragment>
                  ))}
                {append && append[v.name] && (
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={append[v.name]}
                    sx={{ mt: 2 }}
                  >
                    {v.btnName ?? "Tạo thêm"}
                  </Button>
                )}
              </Grid>
            );
          })}
        </Grid>
        {!hiddenButton && (
          <Grid
            mt={3}
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: props?.colorText, color: btnColor, ...actionBtn }}
              fullWidth={btnFullWidth}
            >
              {txtConfirmBtn ?? "Xác nhận"}
            </Button>
          </Grid>
        )}
      </form>
    </Grid>
  );
}
